import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import WbSunnyRoundedIcon from "@mui/icons-material/WbSunnyRounded";
import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SHIFT_WINDOW } from "../../theme/tokens";
import { getShiftTypeFromStartTime } from "../../utils/dateUtils";
import { ShiftType } from "../../utils/models";
import { LedgerLine } from "../UIToolkit/molecules/LedgerLine";
import { useBreakdownPanel } from "./BreakdownPanelContext";
import { formatTimeRange, moneyOrHidden } from "./consts";
import { RateTypeCell } from "./RateTypeCell";
import { RuleCard } from "./RuleCard/RuleCard";
import { RuleNameText } from "./RuleCard/RuleNameText";
import { type RuleCardSubject, segmentCardSubjects } from "./RuleCard/ruleCardSubjects";
import { SegmentComposition } from "./SegmentComposition";
import { generalSegmentLabel } from "./segmentLabel";
import { useCompositionText } from "./useCompositionText";
import { toCents, type WageSegment } from "./wageComposition";

type Props = {
	segment: Extract<WageSegment, { type: "paid" }>;
	compositionOpen: boolean;
	/** The segment's composition region id (named by the fold toggle's `aria-controls`). */
	regionId: string;
	/** Set on a rate-type-only segment of a breakdown whose rows disagree on base: nothing else states its base. */
	showOwnBase: boolean;
	agencyName: string | null;
	canViewWage: boolean;
	canOpenWageSettings: boolean;
};

/** The segment's general name, opening a card with every configuration row behind the segment. */
const segmentName = (rowId: number, name: string, subjects: RuleCardSubject[]): ReactNode =>
	subjects.length === 0 ? (
		name
	) : (
		<RuleNameText
			label={name}
			card={<RuleCard subjects={subjects} dataTestId={`wage-breakdown-segment-card-${rowId}`} />}
			dataTestId={`wage-breakdown-segment-name-${rowId}`}
		/>
	);

/**
 * A stretch of hours paid at one rate, and folded beneath it the lines that built that rate. A row shown
 * as a rate type only (`lines` null) keeps DAT-905's linked rate-type label instead.
 */
export const PaidSegmentView: FC<Props> = ({ segment, compositionOpen, regionId, showOwnBase, agencyName, canViewWage, canOpenWageSettings }) => {
	const { t } = useTranslation();
	const text = useCompositionText();
	const { row, lines } = segment;
	const hours = t("wageBreakdown.segment.hours", { hours: row.totalHours.toFixed(2) });
	const ownBase = showOwnBase ? [t("wageBreakdown.segment.base", { amount: moneyOrHidden(toCents(row.base), canViewWage) })] : [];
	const isDay = getShiftTypeFromStartTime(row.startTime) === ShiftType.Day;
	const { isAgency, access } = useBreakdownPanel();
	// The agency's row is read from Agency Configuration: only a viewer who could open it gets it in a card.
	const agencyInCard = isAgency && access.canOpenAgencyConfiguration;
	return (
		<Box
			component="li"
			data-testid={`wage-breakdown-subtable-row-${row.id}`}
			data-segment-type={lines === null ? "legacy" : "paid"}
			sx={{ listStyle: "none", m: 0, p: 0 }}
		>
			<LedgerLine
				component="div"
				// The same sun/moon language as the staffing grid.
				leading={isDay ? <WbSunnyRoundedIcon sx={{ color: SHIFT_WINDOW.DAY_RAIL }} /> : <DarkModeRoundedIcon sx={{ color: SHIFT_WINDOW.NIGHT_RAIL }} />}
				label={
					lines === null ? (
						<RateTypeCell rowId={row.id} rateType={row.rateType} linkable={canOpenWageSettings} includeAgency={agencyInCard} />
					) : (
						segmentName(row.id, generalSegmentLabel(lines, row.startTime), segmentCardSubjects(lines, agencyInCard))
					)
				}
				detail={[formatTimeRange(row.startTime, row.endTime), hours, ...ownBase].join(text.separator)}
				rate={moneyOrHidden(segment.rateCents, canViewWage)}
				rateUnit={canViewWage ? t("wageBreakdown.perHourUnit") : undefined}
				amount={moneyOrHidden(segment.wageCents, canViewWage)}
			/>
			{lines !== null && (
				<SegmentComposition
					row={row}
					lines={lines}
					open={compositionOpen}
					regionId={regionId}
					agencyName={agencyName}
					canViewWage={canViewWage}
					canOpenWageSettings={canOpenWageSettings}
				/>
			)}
		</Box>
	);
};
